import { useState, useMemo } from "react"
import { Chart, TableVisualization } from "~/ui-rtk/components/ui/charts"
import { DateRangePicker, MultiSelect } from "~/ui-rtk/components/ui/controls"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { usePeriod, DATE_FORMAT, isValidDate } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import {
  VISUALIZATION_TYPE,
  VISUALIZATIONS_MAP,
} from "~/ui-rtk/constants/charts"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import useSources from "~/ui-rtk/hooks/sources"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"

const dayjs = useDate()

const TAB_CONFIG = [
  {
    label: "Page Paths",
    widgetId: "company_web_analytics_pre_atc_page_paths_table",
    urlPrefix: "pre_atc_page_paths",
    nameDimension: "pre_add_to_cart_page_path",
    sortKey:
      "company_web_analytics_page_value_pre_add_to_cart_page_paths.session_count",
  },
  {
    label: "Collections",
    widgetId: "company_web_analytics_pre_atc_collections_table",
    urlPrefix: "pre_atc_collections",
    nameDimension: "pre_add_to_cart_collection",
    sortKey:
      "company_web_analytics_page_value_pre_add_to_cart_collections.session_count",
  },
  {
    label: "Products",
    widgetId: "company_web_analytics_pre_atc_products_table",
    urlPrefix: "pre_atc_products",
    nameDimension: "pre_add_to_cart_product",
    sortKey:
      "company_web_analytics_page_value_pre_add_to_cart_products.session_count",
  },
] as const

const TAB_OPTIONS = TAB_CONFIG.map(({ label }) => ({ label }))

export default function PreAtcAnalysisPage() {
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const { dateRange, compareRange } = useMinMaxDateRange()
  const { granularity, setPeriod, setComparePeriod } = usePeriod()
  const { sources } = useSources()

  const activeTab = TAB_CONFIG[activeTabIndex]
  const tableProps = useMemo(
    () => VISUALIZATIONS_MAP[activeTab.widgetId]?.props,
    [activeTab.widgetId],
  )

  const defaultColumnKeys = useMemo(
    () =>
      tableProps?.columnConfig
        .filter(
          (column: TableVisualizationColumn & { disabled?: boolean }) =>
            !column.disabled,
        )
        .map((column: TableVisualizationColumn) => column.key) || [],
    [tableProps],
  )

  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    activeTab.urlPrefix,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(() => {
    const options = tableProps?.columnConfig.map(
      (column: TableVisualizationColumn) =>
        ({
          value: column.key,
          label: column.header,
        }) as TMultiSelectOption,
    )
    return options || []
  }, [tableProps])

  const columnConfigFiltered = useMemo(
    () =>
      (tableProps?.columnConfig ?? [])
        .filter(({ key }: { key: string }) =>
          (columnKeys as string[])?.includes(key),
        )
        .map(
          ({
            disabled,
            ...col
          }: TableVisualizationColumn & { disabled?: boolean }) => col,
        ),
    [tableProps?.columnConfig, columnKeys],
  )

  const changePeriod = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  return (
    <div className="px-4">
      <div className="flex items-center justify-between">
        <Breadcrumbs
          items={[
            {
              name: "Web Merchandising",
              slug: brandRoutes.WEB_MERCHANDISING.ROOT,
            },
            {
              name: "Pre ATC Analysis",
              slug: brandRoutes.WEB_MERCHANDISING.PRE_ATC_ANALYSIS,
            },
          ]}
        />
      </div>

      <div className="mt-system-lg mb-system-md">
        <TabsGroup
          options={TAB_OPTIONS}
          index={activeTabIndex}
          onChange={setActiveTabIndex}
        />
      </div>

      <div className="flex justify-end items-stretch gap-system-sm mb-system-lg">
        <MultiSelect
          label="Columns"
          options={columnOptions as TMultiSelectOption[]}
          value={columnOptions.filter((option: TMultiSelectOption) =>
            (columnKeys as string[]).includes(option.value),
          )}
          onChange={(options: TMultiSelectOption[]) =>
            setColumnKeys(options.map(({ value }) => value))
          }
        />
        <TableFiltersBar
          id={activeTab.urlPrefix}
          columnConfig={columnConfigFiltered}
        />
        {dateRange?.[0] &&
          dateRange?.[1] &&
          isValidDate(dateRange[0]) &&
          isValidDate(dateRange[1]) && (
            <DateRangePicker
              start={dateRange[0]}
              end={dateRange[1]}
              compareStart={
                compareRange?.[0] && isValidDate(compareRange[0])
                  ? compareRange[0]
                  : undefined
              }
              compareEnd={
                compareRange?.[1] && isValidDate(compareRange[1])
                  ? compareRange[1]
                  : undefined
              }
              comparisonEnabled={true}
              onChange={changePeriod}
            />
          )}
      </div>

      <div className="mt-3">
        <Chart
          {...tableProps}
          chartId={activeTab.widgetId}
          urlParamsPrefix={activeTab.urlPrefix}
          className=""
          widgetType={VISUALIZATION_TYPE.TableVisualization}
          component={TableVisualization}
          dateRange={dateRange}
          compareRange={compareRange ?? undefined}
          comparisonEnabled={true}
          compareUniqueKey={`${activeTab.nameDimension}`}
          granularity={granularity}
          sources={sources}
          hideAggSelector={true}
          columnConfig={columnConfigFiltered}
          enableCSVExport={true}
          csvExportFilename={activeTab.urlPrefix}
          defaultSorting={[
            {
              id: activeTab.sortKey,
              desc: true,
            },
          ]}
        />
      </div>
    </div>
  )
}
